import React from 'react';
import PropTypes from 'prop-types';

import './availability.css';

import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';

import { WeezHeader, MiniTab, SplitContainer, Card, Button, Search, ButtonDropdown, Icon, ToastContext, Loader, DropdownItem, TotalFilters } from '@weezevent/nacre';

import AvailabilityModel from '../../models/availability';
import { ZoneInventoryModel } from '../../models';
import Contingent from '../../models/contingentModel';
import Category from '../../models/category';
import Rate from '../../models/rateModel';

import InventoryPicker from '../../components/contingent/inventoryPicker';
import { SeatMap } from '../placement/map';
import { SeatStyles as SeatSVGStyles } from '../placement/seatStyles';
import useSeatedContext from '../../context/seatedContext';
import LoadingCard from '../../components/loadingCard';
import ExportButton from '../../components/export/exportButton';
import { usePermissions } from '../../user/permissions';
import AvailabilityQuotaCard from './AvailabilityQuotaCard';

const SeatStyles = ({ type = 'contingents', contingents, categories }) => {
    if (type === 'contingents') {
        return <SeatSVGStyles items={contingents} query="data-contingent-id" />;
    }

    if (type === 'categories') {
        return <SeatSVGStyles items={categories} query="data-category-id" />;
    }

    return null;
};

export default function Availability({ event, organization, history, zoneId = '' }) {
    const [display, setDisplay] = React.useState('contingents');
    const [searchZone, setSearchZone] = React.useState('');
    const [{ inventory, inventories }] = useSeatedContext();
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const [currentAvailability, setCurrentAvailability] = React.useState(null);
    const [loadingAvailability, setLoadingAvailability] = React.useState(false);
    const [availabilityError, setAvailabilityError] = React.useState(false);
    const [refreshingCache, setRefreshingCache] = React.useState(false);

    // Determine if we're in detail view
    const isDetailView = zoneId && zoneId !== 'root';

    const updateURL = React.useCallback(
        (zone, new_inventory = null) => {
            let inventory_id = (new_inventory || inventory).uuid;
            const zonePk = zone?.pk || zone?.uuid || 'root';
            history.push(`/ticket/O${organization.id}/E${event.id}/seating/availability/inventory/${inventory_id}/zone/${zonePk}`);
        },
        [organization, event, inventory, history]
    );

    const [contingents, loadingContingents] = Contingent.useApiModel({
        organizationId: organization.id,
        eventId: event.id
    });

    const [categories, loadingCategories] = Category.useApiModel({
        organizationId: organization.id,
        chartId: event.chart
    });

    const [rates, loadingRates] = Rate.useApiModel({
        organizationId: organization.id,
        eventId: event.id
    });

    // Fetch zones list when not in detail view
    const [zones, loadingZones, syncZones, zonesErrors] = ZoneInventoryModel.useApiModel(
        {
            organizationId: organization.id,
            chartId: event.chart,
            inventoryId: inventory?.uuid,
            launch: !isDetailView && Boolean(inventory)
        },
        [inventory?.uuid]
    );

    const prevZoneIdRef = React.useRef(zoneId);

    React.useEffect(() => {
        // Refetch zones if we return to root zone
        if (zoneId === 'root' && prevZoneIdRef.current !== 'root') {
            syncZones();
        }
        prevZoneIdRef.current = zoneId;
    }, [zoneId, syncZones]);

    // Fetch zone availability
    const fetchAvailability = React.useCallback(
        (queryParams = {}) => {
            if (!inventory) {
                setCurrentAvailability(null);
                setAvailabilityError(false);
                return;
            }

            setLoadingAvailability(true);
            setAvailabilityError(false);
            AvailabilityModel.getAll({
                organizationId: organization.id,
                eventId: event.id,
                inventoryId: inventory.uuid,
                zoneId: isDetailView ? zoneId : null,
                cache: false,
                query: queryParams
            })
                .then(av => {
                    setCurrentAvailability(av);
                    setLoadingAvailability(false);
                    setAvailabilityError(false);
                })
                .catch(e => {
                    Sentry.configureScope(scope => {
                        scope.setExtra('zone_id', zoneId);
                    });
                    Sentry.captureException(e);
                    setAvailabilityError(true);
                    setLoadingAvailability(false);
                });
        },
        [isDetailView, inventory, zoneId, organization, event, toast, t]
    );

    // Reload availability on inventory change
    React.useEffect(() => {
        if (!inventory) {
            return;
        }

        fetchAvailability();
    }, [fetchAvailability, inventory]);

    const onPreviousZone = React.useCallback(() => {
        updateURL({ pk: 'root' });
    }, [updateURL]);

    const onZoneSelected = React.useCallback(
        zone => {
            updateURL(zone);
        },
        [updateURL]
    );

    const changeInventory = React.useCallback(
        new_inventory => {
            if (new_inventory.uuid === inventory.uuid) {
                return;
            }
            updateURL({ pk: zoneId || 'root' }, new_inventory);
        },
        [updateURL, inventory, zoneId]
    );

    const loadings = React.useMemo(() => loadingRates || loadingContingents || loadingCategories || (isDetailView ? loadingAvailability : loadingZones), [
        isDetailView,
        loadingRates,
        loadingCategories,
        loadingContingents,
        loadingZones,
        loadingAvailability
    ]);

    const exportPath = React.useMemo(() => {
        return `/ticket/organizations/${organization.pk}/events/${event.id}/inventories/${inventory.uuid}/export/`;
    }, [organization, event, inventory]);

    const handleRefreshCache = React.useCallback(async () => {
        setRefreshingCache(true);
        try {
            await AvailabilityModel.refreshCache({
                organizationId: organization.id,
                chartId: event.chart,
                inventoryId: inventory.uuid
            });
            toast.success(t('ticket.availability.refresh_cache_triggered'));
        } catch (e) {
            toast.error(t('ticket.availability.refresh_cache_error'));
        } finally {
            setRefreshingCache(false);
        }
    }, [organization, event, inventory]);

    const headerTitle = React.useMemo(() => {
        if (!isDetailView) {
            return t('main_menu_configure_availability');
        }
        return currentAvailability?.name || <Loader size="tiny" inline />;
    }, [isDetailView, currentAvailability?.name]);

    return (
        <div className="placement">
            <WeezHeader
                title={headerTitle}
                backLink={isDetailView ? { onClick: onPreviousZone } : undefined}
                rightComponent={[
                    <ButtonDropdown
                        key="dropdown"
                        right
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <ExportButton
                                key="export_seats"
                                path={exportPath}
                                organization={organization}
                                eventIds={[event.id]}
                                choiceProcess="seat_inventories"
                                showButton={false}
                                buttonTrans="ticket.availability.export_seats"
                                onError={() => toast.error(t('ticket.export.download_state_error'))}
                            />,
                            <ExportButton
                                key="export_reservations"
                                path={exportPath}
                                organization={organization}
                                eventIds={[event.id]}
                                choiceProcess="bookings"
                                showButton={false}
                                buttonTrans="ticket.availability.export_reservations"
                                onError={() => toast.error(t('ticket.export.download_state_error'))}
                            />,
                            permissions.isAdmin() && (
                                <DropdownItem item={t('ticket.availability.refresh_cache')} onClick={handleRefreshCache} isLoading={refreshingCache} disabled={refreshingCache} />
                            )
                        ].filter(Boolean)}
                    />
                ]}
            />
            <SeatStyles type={display} categories={categories} contingents={contingents} />
            <SplitContainer
                additional={
                    <AvailabilityQuotaCard
                        display={display}
                        loadingAvailability={loadingAvailability}
                        availabilityError={availabilityError}
                        fetchAvailability={fetchAvailability}
                        currentAvailability={currentAvailability}
                        contingents={contingents}
                        categories={categories}
                        event={event}
                        organization={organization}
                        inventory={inventory}
                    />
                }
            >
                <Card>
                    <Card.Header>
                        <Card.Title title={isDetailView ? t('ticket.availability.headerTitle') : t('ticket.common.seating.zones')} />
                    </Card.Header>
                    <Card.Content className="availability-card-content">
                        <LoadingCard loading={loadings}>
                            <div className="availability-controls">
                                <div className="availability-filters">
                                    <InventoryPicker currentInventory={inventory} inventories={inventories} onChange={changeInventory} />
                                    {!isDetailView && (
                                        <Search
                                            onChange={(synth, value) => {
                                                setSearchZone(value || '');
                                            }}
                                            handleReset={() => setSearchZone('')}
                                            placeholder={t('ticket.availability.search_placeholder')}
                                            value={searchZone}
                                        />
                                    )}
                                </div>
                                <div className="availability-display-controls">
                                    <MiniTab
                                        panes={[
                                            { label: t('ticket.availability.show_contingents'), value: 'contingents' },
                                            { label: t('ticket.availability.show_categories'), value: 'categories' }
                                        ]}
                                        activeTab={display}
                                        onTabSelected={tab => {
                                            setDisplay(tab.value);
                                        }}
                                    />
                                    {!isDetailView && (
                                        <TotalFilters labelTotal={t('ticket.availability.zone_count_label', { count: zones?.length || 0 })} total={zones?.length || 0} />
                                    )}
                                </div>
                            </div>
                            <SeatMap
                                zones={zones}
                                zonesErrors={zonesErrors}
                                zoneId={zoneId}
                                eventId={event.id}
                                chartId={event.chart}
                                organization={organization}
                                availability={currentAvailability}
                                contingents={contingents}
                                categories={categories}
                                rates={rates}
                                searchZone={searchZone}
                                sync={() => fetchAvailability({ force_cache_refresh: 1 })}
                                onBack={onPreviousZone}
                                onZoneSelected={onZoneSelected}
                                display={display}
                            />
                        </LoadingCard>
                    </Card.Content>
                </Card>
            </SplitContainer>
        </div>
    );
}

Availability.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    zoneId: PropTypes.string.isRequired
};
